import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Card, CardHeader } from '@truecare/ui';
import type {
  AdminCaregiverListItem,
  AdminCaregiverListResponse,
  AdminFacilityListItem,
  AdminFacilityListResponse,
} from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { adminBeforeLoad } from '../lib/admin-before-load';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';

/**
 * Phase 14d-2 — Vetting queue page (Phase 17 — re-scoped to onboarding only).
 *
 * Three onboarding sub-queues stacked on a single page: getting NEW
 * applicants (professionals + facilities) through the gate. Ongoing
 * compliance monitoring of the EXISTING active roster used to live here too,
 * which conflated two different operator jobs — that moved to /compliance.
 * Click any row → the persona detail page where the actual approve/reject
 * buttons live (per 14b — actions surface on detail pages, not the queue).
 *
 *   1. Care professionals awaiting review — `?status=UNDER_REVIEW`. The
 *      operator's morning vetting pass over submitted applications.
 *   2. Facilities awaiting contract setup — `?status=PROSPECT`. Lead
 *      capture from `/for-facilities` lands here; operator clicks
 *      through to detail to attach a signed contract.
 *   3. Applicants in progress — `?status=APPLYING`. Care professionals who
 *      started but haven't submitted yet (nothing to action — visibility so
 *      a stalled applicant isn't invisible / can be nudged). Bug #2 (Phase
 *      17): before this, an in-progress applicant appeared nowhere and it
 *      looked like they'd vanished.
 *
 * All sections are server-paginated; v1 scale (~10s of rows) fits in
 * the default 25-row page. Sorting deferred to v1.x.
 */

export const Route = createFileRoute('/vetting')({
  beforeLoad: () => adminBeforeLoad('/vetting'),
  component: VettingQueuePage,
});

function VettingQueuePage() {
  return (
    <AdminLayout>
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-forest md:text-4xl">Vetting queue</h1>
        <p className="font-body text-sm text-ink-muted">
          New applicants waiting to get into the marketplace — care-professional
          applications and facility contracts to review, plus applications still
          in progress. Click any row to open it and approve. (Ongoing compliance
          checks on the active roster live under Compliance.)
        </p>
      </header>

      <CaregiverApplicationsSection />
      <FacilityContractsSection />
      <ApplicantsInProgressSection />
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section 1 — Caregivers awaiting review
// ─────────────────────────────────────────────────────────────────────

const CAREGIVER_COLUMNS: AdminTableColumn<AdminCaregiverListItem>[] = [
  {
    key: 'legalSurname',
    header: 'Name',
    sortable: false,
    render: (r) => `${r.preferredName ?? r.legalGivenName} ${r.legalSurname}`,
  },
  { key: 'email', header: 'Email', sortable: false },
  {
    key: 'declaredRoleCodes',
    header: 'Roles',
    width: 'w-32',
    sortable: false,
    render: (r) =>
      r.declaredRoleCodes.length > 0
        ? r.declaredRoleCodes.join(', ')
        : '—',
  },
  {
    key: 'applicationSubmittedAt',
    header: 'Submitted',
    width: 'w-40',
    sortable: false,
    render: (r) =>
      r.applicationSubmittedAt
        ? new Date(r.applicationSubmittedAt).toLocaleDateString('en-CA')
        : '—',
  },
];

function CaregiverApplicationsSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useQuery<AdminCaregiverListResponse>({
    queryKey: ['admin', 'vetting', 'caregivers-under-review', page],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers' as never, {
          params: { query: { status: 'UNDER_REVIEW', page, pageSize: 25 } },
        } as never),
      )) as unknown as AdminCaregiverListResponse,
    placeholderData: keepPreviousData,
  });

  return (
    <Card padding="md" className="mt-6" data-testid="vetting-caregivers-card">
      <CardHeader
        title={`Care professionals awaiting review${
          query.data ? ` (${query.data.total})` : ''
        }`}
        description="Applications submitted for operator review. Open the detail page to approve credentials + compliance docs + activate the care professional."
      />
      <AdminTable<AdminCaregiverListItem>
        columns={CAREGIVER_COLUMNS}
        rows={query.data?.items ?? []}
        isLoading={query.isPending}
        isError={query.isError}
        emptyMessage="No care professionals awaiting review."
        page={page}
        limit={25}
        totalCount={query.data?.total ?? 0}
        onPageChange={setPage}
        onRowClick={(r) =>
          navigate({ to: '/caregivers/$id', params: { id: r.id } })
        }
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section 2 — Facilities awaiting contract setup
// ─────────────────────────────────────────────────────────────────────

const FACILITY_COLUMNS: AdminTableColumn<AdminFacilityListItem>[] = [
  { key: 'name', header: 'Facility', sortable: false },
  { key: 'type', header: 'Type', width: 'w-32', sortable: false },
  { key: 'city', header: 'City', width: 'w-32', sortable: false },
  {
    key: 'createdAt',
    header: 'Lead received',
    width: 'w-40',
    sortable: false,
    render: (r) => new Date(r.createdAt).toLocaleDateString('en-CA'),
  },
];

function FacilityContractsSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useQuery<AdminFacilityListResponse>({
    queryKey: ['admin', 'vetting', 'facilities-prospect', page],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/facilities' as never, {
          params: { query: { status: 'PROSPECT', page, pageSize: 25 } },
        } as never),
      )) as unknown as AdminFacilityListResponse,
    placeholderData: keepPreviousData,
  });

  return (
    <Card padding="md" className="mt-6" data-testid="vetting-facilities-card">
      <CardHeader
        title={`Facilities awaiting contract setup${
          query.data ? ` (${query.data.totalCount})` : ''
        }`}
        description="Facility leads (from /for-facilities lead capture) awaiting contract signing. Open the detail page to record contract terms."
      />
      <AdminTable<AdminFacilityListItem>
        columns={FACILITY_COLUMNS}
        rows={query.data?.items ?? []}
        isLoading={query.isPending}
        isError={query.isError}
        emptyMessage="No facility leads awaiting contract."
        page={page}
        limit={25}
        totalCount={query.data?.totalCount ?? 0}
        onPageChange={setPage}
        onRowClick={(r) =>
          navigate({ to: '/facilities/$id', params: { id: r.id } })
        }
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section 3 — Applicants in progress (not yet submitted) — bug #2
// ─────────────────────────────────────────────────────────────────────

// APPLYING caregivers have no applicationSubmittedAt yet, so the shared
// "Submitted" column would be all "—"; use account-created ("Started") instead.
const IN_PROGRESS_COLUMNS: AdminTableColumn<AdminCaregiverListItem>[] = [
  {
    key: 'legalSurname',
    header: 'Name',
    sortable: false,
    render: (r) => `${r.preferredName ?? r.legalGivenName} ${r.legalSurname}`,
  },
  { key: 'email', header: 'Email', sortable: false },
  {
    key: 'declaredRoleCodes',
    header: 'Roles',
    width: 'w-32',
    sortable: false,
    render: (r) =>
      r.declaredRoleCodes.length > 0 ? r.declaredRoleCodes.join(', ') : '—',
  },
  {
    key: 'createdAt',
    header: 'Started',
    width: 'w-40',
    sortable: false,
    render: (r) => new Date(r.createdAt).toLocaleDateString('en-CA'),
  },
];

function ApplicantsInProgressSection() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const query = useQuery<AdminCaregiverListResponse>({
    queryKey: ['admin', 'vetting', 'caregivers-applying', page],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers' as never, {
          params: { query: { status: 'APPLYING', page, pageSize: 25 } },
        } as never),
      )) as unknown as AdminCaregiverListResponse,
    placeholderData: keepPreviousData,
  });

  return (
    <Card padding="md" className="mt-6" data-testid="vetting-applicants-in-progress-card">
      <CardHeader
        title={`Applicants in progress${
          query.data ? ` (${query.data.total})` : ''
        }`}
        description="Care professionals who signed up but haven't submitted for review yet — nothing to approve here. Open one to see how far along they are, or to nudge a stalled applicant."
      />
      <AdminTable<AdminCaregiverListItem>
        columns={IN_PROGRESS_COLUMNS}
        rows={query.data?.items ?? []}
        isLoading={query.isPending}
        isError={query.isError}
        emptyMessage="No applications in progress."
        page={page}
        limit={25}
        totalCount={query.data?.total ?? 0}
        onPageChange={setPage}
        onRowClick={(r) =>
          navigate({ to: '/caregivers/$id', params: { id: r.id } })
        }
      />
    </Card>
  );
}
